import React, { useCallback, useEffect, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { AlertCircle, ExternalLink, Sparkles, ChevronDown, ChevronRight } from "lucide-react";
import { formatINR } from "./shared";

export default function ResultsPanel() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/results/leaderboard");
      setData(data);
    } catch (err) { toast.error(errorMessage(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRank = async (rank, team_id) => {
    setBusy(true);
    try {
      await api.post("/admin/results/set-rank", { rank, team_id });
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const announce = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/results/announce", {});
      toast.success("Results announced! Emails are being sent.");
      setConfirming(false);
      await load();
      navigator.clipboard?.writeText(res.results_url).catch(() => {});
    } catch (err) { toast.error(errorMessage(err)); setConfirming(false); } finally { setBusy(false); }
  };

  const toggleFinalists = async () => {
    setBusy(true);
    try {
      await api.post("/admin/results/show-finalists", { enabled: !data.show_finalists });
      toast.success(!data.show_finalists ? "Finalists section is now public" : "Finalists section hidden");
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  if (!data) return <div className="card-surface text-gray-500">Loading leaderboard…</div>;

  const pending = data.pending_ranks || {};
  const allConfirmed = ["1", "2", "3"].every(r => pending[r]);
  const dateSet = !!data.results_announce_at;
  const announced = data.announced;
  const teamById = Object.fromEntries(data.teams.map(t => [t.team_id, t]));
  const rankEmoji = { 1: "🥇", 2: "🥈", 3: "🥉" };

  return (
    <div className="space-y-6" data-testid="results-panel">
      {/* Announce status / control */}
      <div className="card-surface">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-mono mb-1">Results Announcement</div>
            {announced ? (
              <div className="text-emerald-400 font-medium" data-testid="results-announced-state">
                Results Announced — {new Date(data.announced_at).toLocaleString()}
              </div>
            ) : (
              <div className="text-gray-400 text-sm">Confirm Rank 1, 2 & 3 below, then announce. This sends all emails and goes live publicly.</div>
            )}
            {!dateSet && !announced && (
              <div className="mt-2 flex items-center gap-2 text-amber-400 text-sm" data-testid="no-date-warning">
                <AlertCircle className="w-4 h-4" /> Set the "Results Announcement Date" in Status → Timeline before announcing.
              </div>
            )}
            <div className="mt-3 flex items-center gap-2 text-xs">
              <span className="text-gray-500">Public URL:</span>
              {announced ? (
                <a href={data.results_url} target="_blank" rel="noopener noreferrer" className="text-brand font-mono flex items-center gap-1" data-testid="results-url">
                  {data.results_url} <ExternalLink className="w-3 h-3" />
                </a>
              ) : <span className="text-gray-600 font-mono">{data.results_url} (live after announce)</span>}
            </div>
            <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="show-finalists-row">
              <button
                onClick={toggleFinalists}
                disabled={busy}
                role="switch"
                aria-checked={!!data.show_finalists}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${data.show_finalists ? "bg-brand" : "bg-white/15"}`}
                data-testid="show-finalists-toggle">
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${data.show_finalists ? "translate-x-4" : "translate-x-1"}`} />
              </button>
              <div className="text-xs">
                <span className="text-gray-300">Show "Round 2 Finalists" on public results page</span>
                <span className={`ml-2 font-mono ${data.show_finalists ? "text-emerald-400" : "text-gray-500"}`}>{data.show_finalists ? "ON" : "OFF (default)"}</span>
              </div>
            </div>
          </div>
          {announced ? (
            <button disabled className="btn-secondary opacity-60 cursor-not-allowed" data-testid="announce-done-btn">Results Announced</button>
          ) : (
            <button onClick={() => setConfirming(true)} disabled={!allConfirmed || !dateSet || busy}
              className="btn-primary flex items-center gap-2" data-testid="announce-results-btn">
              <Sparkles className="w-4 h-4" /> Announce Results
            </button>
          )}
        </div>

        {/* Confirmed ranks summary */}
        {(pending["1"] || pending["2"] || pending["3"]) && (
          <div className="mt-5 grid sm:grid-cols-3 gap-3" data-testid="confirmed-ranks">
            {[1, 2, 3].map(r => {
              const t = teamById[pending[String(r)]];
              const prize = (data.prizes.find(p => p.rank === r) || {}).amount;
              return (
                <div key={r} className={`border p-3 ${t ? "border-brand/40 bg-brand/5" : "border-white/10 border-dashed"}`}>
                  <div className="text-lg">{rankEmoji[r]} Rank {r}</div>
                  {t ? (
                    <>
                      <div className="text-white font-medium mt-1">{t.team_name}</div>
                      <div className="text-xs text-gray-400">{t.project_title}</div>
                      <div className="text-xs text-gray-500">{t.college}</div>
                      <div className="text-xs text-brand mt-1">Score {t.avg_score ?? "—"} · {formatINR(prize)}</div>
                    </>
                  ) : <div className="text-gray-600 text-sm mt-1">Not confirmed</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Leaderboard */}
      <div className="card-surface overflow-x-auto">
        <div className="label-mono mb-3">Round 2 Leaderboard — sorted by score</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="label-mono text-left border-b border-white/10">
              <th className="py-3 pr-4">#</th>
              <th className="py-3 pr-4">Team</th>
              <th className="py-3 pr-4">College</th>
              <th className="py-3 pr-4">Avg Score</th>
              <th className="py-3 pr-4">Rank</th>
              {!announced && <th className="py-3 pr-4">Confirm</th>}
              <th className="py-3 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {data.teams.map((t, i) => (
              <React.Fragment key={t.team_id}>
                <tr className="border-b border-white/5" data-testid={`lb-row-${t.team_id}`}>
                  <td className="py-3 pr-4 text-gray-500 font-mono">{i + 1}</td>
                  <td className="py-3 pr-4 text-white">{t.team_name}<div className="text-xs text-gray-500">{t.project_title}</div></td>
                  <td className="py-3 pr-4 text-gray-400">{t.college}</td>
                  <td className="py-3 pr-4 font-mono text-brand font-semibold">{t.avg_score ?? "—"}<span className="text-gray-500 text-xs"> · {t.judges_count}j</span></td>
                  <td className="py-3 pr-4">{t.rank ? <span className="badge badge-finalist">{rankEmoji[t.rank]} Rank {t.rank}</span> : <span className="text-gray-600">—</span>}</td>
                  {!announced && (
                    <td className="py-3 pr-4">
                      <div className="flex gap-1">
                        {[1, 2, 3].map(r => (
                          <button key={r} disabled={busy} onClick={() => setRank(r, t.rank === r ? null : t.team_id)}
                            className={`px-2 py-1 text-xs font-mono border ${t.rank === r ? "bg-brand text-black border-brand" : "border-white/15 text-gray-400 hover:text-white"}`}
                            data-testid={`confirm-rank-${r}-${t.team_id}`}>{r}</button>
                        ))}
                      </div>
                    </td>
                  )}
                  <td className="py-3 pr-4">
                    <button onClick={() => setExpanded(expanded === t.team_id ? null : t.team_id)} className="text-gray-400 hover:text-brand" data-testid={`expand-${t.team_id}`}>
                      {expanded === t.team_id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    </button>
                  </td>
                </tr>
                {expanded === t.team_id && (
                  <tr className="border-b border-white/5 bg-white/[0.02]">
                    <td colSpan={announced ? 6 : 7} className="py-3 px-4">
                      <div className="text-xs text-gray-400 mb-2">Members: {t.members.join(", ") || "—"}</div>
                      {t.judge_scores.length === 0 ? <div className="text-gray-600 text-sm">No judge scores yet.</div> : (
                        <div className="grid sm:grid-cols-2 gap-2">
                          {t.judge_scores.map((js, k) => (
                            <div key={k} className="border border-white/10 p-2.5 text-xs">
                              <div className="flex justify-between"><span className="text-white">{js.judge_name}</span><span className="text-brand font-mono">{js.weighted_total}/10</span></div>
                              <div className="text-gray-500 mt-1">Innovation {js.innovation} · Impact {js.impact} · Feasibility {js.feasibility} · Execution {js.execution} · Presentation {js.presentation}</div>
                              {js.notes && <div className="text-gray-400 mt-1 italic">"{js.notes}"</div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {data.teams.length === 0 && <tr><td colSpan={7} className="text-center text-gray-500 py-8">No Round 2 submissions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {confirming && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-5" data-testid="announce-confirm-modal">
          <div className="card-surface max-w-md w-full">
            <h2 className="font-display text-2xl text-white">Announce results?</h2>
            <p className="text-gray-400 mt-3">This will publicly announce results and send all winner & participant emails. <b className="text-white">This cannot be undone.</b> Confirm?</p>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setConfirming(false)} className="btn-secondary flex-1" data-testid="cancel-announce">Cancel</button>
              <button onClick={announce} disabled={busy} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="confirm-announce">
                {busy ? <span className="spinner" /> : <Sparkles className="w-4 h-4" />} Yes, announce
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
